import React, {useState, useEffect, useMemo}		from 'react';
import {useNavigate, useParams}						from 'react-router-dom';
import moment										from 'moment';
import {useReactTable, ColumnDef, getCoreRowModel,
	getFilteredRowModel, getSortedRowModel, getFacetedRowModel,
	getFacetedUniqueValues, getFacetedMinMaxValues} from '@tanstack/react-table';

import DevicesHistorical							from '../Devices/DevicesHistorical';
import ContractDocument								from './ContractsDocuments';
import DevicesBadge									from '../ressources/components/Badge/DevicesBadges';
import Button										from '../ressources/components/Button';
import Input										from '../ressources/components/Input';
import MultiInputContainer							from '../ressources/components/Input/MultiInputContainer';
import Tabs											from '../ressources/components/Tabs';
import Textarea										from '../ressources/components/Textarea';
import Icon											from '../ressources/components/Icon';
import {DeviceCard}									from '../ressources/components/Device';
import {ContractStatus}								from '../ressources/components/Contract';
import ModalFilter									from '../ressources/components/ModalFilter';
import Badge										from '../ressources/components/Badge';
import Section										from '../ressources/components/Section';
import ButtonStickyContainer						from '../ressources/components/Button/ButtonStickyContainer';
import ModalTags									from '../ressources/components/ModalTags';
import Table										from '../ressources/components/Table';
import DevicesBadges								from '../ressources/components/Badge/DevicesBadges';

import {useAppSelector, useAppDispatch}				from '../app/hooks';
import {getDevicesApi}								from '../redux/devices/devices';
import {getContractsApi, putContractsApi}			from '../redux/contracts/contracts';
import {putContractNotValalaApi}					from '../redux/contractsNotValala/contractsNotValala';
import {AllContract, AllContractUseData, FunderDevice}			from '../redux/contracts/interface';
import {TabsValues}									from '../ressources/components/Tabs/interface';
import {InputValues}								from '../ressources/components/Input/interface';
import {PostContactNotValalaState}					from '../redux/contractsNotValala/interface';
import {SelectData}									from '../ressources/components/Select/interface';
import {FilterChecked}								from '../ressources/components/Modal/interface';
import {Tags}										from '../ressources/components/ModalTags/interface';

import {ft_formatPrice}								from '../ressources/functions/format';
import {ft_genNotification, ft_pushNotification}	from '../ressources/functions/notification';
import {ft_checkContractFilters}					from '../ressources/functions/filters';
import {ft_getContractLocationEnd, ft_getContractStatus,
	ft_isContractNeedAction}						from '../ressources/functions/contractFunction';

import img_filter									from '../ressources/images/icon/filter.svg';
import img_search									from '../ressources/images/icon/search.svg';
import img_faceSmile								from '../ressources/images/icon/face-smile.svg';
import img_file										from '../ressources/images/icon/file.svg';
import img_penLine									from '../ressources/images/icon/pen-line.svg';
import img_dotsVertical								from '../ressources/images/icon/dots-vertical.svg';

import * as API										from '../ressources/API';
import LANGUAGE										from '../ressources/language';
import {LanguageType}								from '../ressources/language/type';

import './Contracts.css';
import {Device} from '../redux/devices/interface';


function Contracts() {
	const dispatch = useAppDispatch();
	const user = useAppSelector(state => state.user.data);
	const contracts = useAppSelector(state => state.contracts.data);
	const contractsNotValala = useAppSelector(state => state.contractsNotValala.data);
	const contractsLoad = useAppSelector(state => state.contracts.isLoad);
	const usersPutLoad = useAppSelector(state => state.companyUsers.isLoadPut);
	const devices = useAppSelector(state => state.devices.data);
	const mobileLines = useAppSelector(state => state.mobileLines.data);
	const lang = useAppSelector(state => state.settings.lang);
	const notificationId = useAppSelector(state => state.notifications.lastId);

	const [view, set_view] = useState('');
	const [indexFunder, set_indexFunder] = useState(0);

	const [load, set_load] = useState(false);
	const [modal, set_modal] = useState<'tags' | 'filter' | null>(null);
	const [tagsList, set_tagsList] = useState<Tags[]>([]);
	const [filters, set_filters] = useState<FilterChecked[]>([]);
	const [search, set_search] = useState<InputValues>('');

	// --- Device Data ---
	const [editData, set_editData] = useState<AllContract | undefined>(undefined);

	const [contractStart, set_contractStart] = useState<InputValues>('');
	const [contractEnd, set_contractEnd] = useState<InputValues>('');
	const [price, set_price] = useState<InputValues>('');
	const [description, set_description] = useState<InputValues>('');

	const [menu, set_menu] = useState<TabsValues>('details');
	const [change, set_change] = useState(false);
	// --- Device Data End ---

	const navigate = useNavigate();
	const params = useParams();
	const TEXTE = LANGUAGE(lang);
	const MENU_DATA = [
		{value: 'details', render: TEXTE.global.details},
		{value: 'documents', render: TEXTE.global.documents}
	];
	const ACTIONS_LIST: SelectData[] = [
		{
			value:  'tags',
			render: <span className={'text-xs weight-500 gray-700'}>{TEXTE.global.editTags}</span>
		}
	];

	const devicesWithLines = useMemo(() => [...devices, ...mobileLines], [devices, mobileLines]);

	const columns = useMemo<ColumnDef<AllContractUseData>[]>(() => [
		{
			size:    30,
			id:      'image',
			cell:    v => (
				<div className={'userCardImg'}>
					<Icon
						size={30}
						iconBorder
						borderSize={0}
						icon={img_file}
					/>
					{ft_isContractNeedAction(v.row.original, v.row.original.i) &&
						<div className={'contractCardAlertIcon'}>{'!'}</div>
					}
				</div>
			)
		},
		{
			id:         'number',
			header:     TEXTE.contract.number,
			accessorFn: row => `${!('name' in row) ? row.number : row.name}${row.i !== undefined ? `-${row.i + 1}` : ''}`
		},
		{
			id:         'date',
			header:     TEXTE.global.date,
			accessorFn: row => row.data?.creationDate || new Date(),
			cell:       v => (v.row.original.data?.creationDate ? moment(v.row.original.data.creationDate).format('DD/MM/YY') : '')
		},
		{
			id:         'status',
			header:     TEXTE.global.status,
			accessorFn: row => ft_getContractStatus(row, row.i),
			cell:       v => <ContractStatus contract={v.row.original} indexFunder={v.row.original.i} />
		},
		{
			id:     'devices',
			header: TEXTE.global.devices,
			cell:   v => <DevicesBadges contract={v.row.original} />
		},
		{
			id:         'price',
			header:     TEXTE.global.price,
			accessorFn: row => ft_formatPrice(row.price)
		},
		{
			id:         'tags',
			header:     TEXTE.global.tags,
			accessorFn: row => row?.tags?.join(' ') || '',
			cell:       v => (
				<div className={'globalDataContainerItemTags'} style={{gap: 4}}>
					{v.row.original?.tags?.map((tag, i) => {
						if (i > 1)
							return;
						if (i === 1 && (v.row.original?.tags?.length || 0) > 2)
							return <Badge key={i} color={'tags'} text={`+ ${(v.row.original?.tags?.length || 0) - 1}`} />;
						return <Badge key={i} color={'tags'} text={tag} />;
					})}
				</div>
			)
		}
	], []);

	const useData = useMemo(() => {
		const result: AllContractUseData[] = [];

		[...contracts, ...contractsNotValala].filter((contract) => {
			if (ft_checkContractFilters(filters, contract)) {
				const isValala = 'funderList' in contract;

				if (!isValala || contract.funderList.length <= 1)
					return result.push({...contract});

				for (let i = 0; i < contract.funderList.length; i++)
					result.push({...contract, i});
			}
		});

		return result;
	}, [contracts, contractsNotValala, filters]);

	const table = useReactTable({
		columns,
		data:  useData,
		state: {
			globalFilter: search
		},
		getCoreRowModel:        getCoreRowModel(),
		getFilteredRowModel:    getFilteredRowModel(),
		getSortedRowModel:      getSortedRowModel(),
		getFacetedRowModel:     getFacetedRowModel(),
		getFacetedUniqueValues: getFacetedUniqueValues(),
		getFacetedMinMaxValues: getFacetedMinMaxValues()
	});

	useEffect(() => {
		dispatch(getDevicesApi(user));
		dispatch(getContractsApi(user));
	}, []);

	useEffect(() => {
		if (params.idContract === 'confirm')
			return set_view(params.idContract);
		const findContact = contracts.find(find => find?._id === params.idContract) || contractsNotValala.find(find => find?._id === params.idContract);

		setContractData(findContact || undefined, parseInt(params.indexFunder || '0', 10));
		set_view(findContact ? 'edit' : '');
		set_indexFunder(parseInt(params.indexFunder || '0', 10));
	}, [params.idContract, params.indexFunder, contracts]);

	useEffect(() => {
		if (editData)
			set_change(true);
	}, [price, contractStart, contractEnd, description]
	);

	useEffect(() => set_change(false), [editData]);

	useEffect(() => {
		if (modal)
			return;

		API.getContractsTags(user)
		.then(response => set_tagsList(response?.data || []));
	}, [modal]);

	function editContract() {
		if (!editData)
			return;

		if ('funderList' in editData) {
			dispatch(putContractsApi({
				user,
				idContract:  editData._id,
				description: description as string
			}))
			.then(() => {
				const notificationData = ft_genNotification(TEXTE, 'editSuccess', img_penLine);
				ft_pushNotification(dispatch, notificationId, notificationData);
			});
		} else {
			const apiData: PostContactNotValalaState = {
				price,
				description,
				locationEnd:   contractEnd ? moment(contractEnd).toDate() : undefined,
				locationStart: contractStart ? moment(contractStart).toDate() : undefined
			};

			dispatch(putContractNotValalaApi({data: apiData, idContract: editData._id, user}))
			.then(() => {
				const notificationData = ft_genNotification(TEXTE, 'editSuccess', img_penLine);
				ft_pushNotification(dispatch, notificationId, notificationData);
			});
		}
	}

	function editTags(newTags: string[]) {
		if (load || !editData)
			return;

		const next = () => {
			set_load(false);

			const notification = ft_genNotification(TEXTE, 'editTagsSuccess', img_filter);

			ft_pushNotification(dispatch, notificationId, notification);
			set_modal(null);
			set_editData({...editData, tags: newTags});
		};

		set_load(true);

		if ('funderList' in editData)
			return dispatch(putContractsApi({user, tags: newTags, idContract: editData._id})).then(next);

		dispatch(putContractNotValalaApi({
			user,
			idContract: editData._id,
			data:       {tags: newTags}
		})).then(next);
	}

	function setContractData(contract: AllContract | undefined, forceIndex: number) {
		if (!contract)
			return set_editData(undefined);

		if ('funderList' in contract) {
			set_contractStart(contract.funderList[forceIndex]?.locationStart ? moment(contract.funderList[forceIndex]?.locationStart).format('DD/MM/YYYY') : '');
			set_price(contract.funderList[forceIndex]?.price);
		} else {
			set_contractStart(contract.locationStart ? moment(contract.locationStart).format('DD/MM/YYYY') : '');
			set_price(contract.price);
		}
		set_contractEnd(ft_getContractLocationEnd(contract, forceIndex));
		set_description(contract.description || '');

		set_editData(contract);
		set_view('edit');
	}

	function renderContractsStatusButton(statusButton: keyof LanguageType['contract']['contractStatus']) {
		return (
			<Button
				fullWidth
				size={'sm'}
				onClick={() => set_filters([{key: 'status', values: [statusButton]}])}
				type={filters.some(f => f.key === 'status' && f.values.includes(statusButton)) ? 'secondary' : 'secondary-gray'}
			>
				{TEXTE.contract.contractStatus[statusButton]}
				<Badge size={'sm'} color={'gray'} text={contracts.filter(f => ft_getContractStatus(f) === statusButton).length} />
			</Button>
		);
	}

	function renderContractsList() {
		let filterCount = 0;

		filters.forEach(filter => filterCount += filter.values.length);

		return (
			<div className={'globalDataContainerItem flex column'}>
				<div className={'flex-row'} style={{gap: 12}}>
					<Button
						fullWidth
						size={'sm'}
						onClick={() => set_filters([])}
						type={filters.some(f => f.key === 'status') ? 'secondary-gray' : 'secondary'}
					>
						{TEXTE.global.all}
						<Badge size={'sm'} color={'gray'} text={contracts.length} />
					</Button>
					{renderContractsStatusButton('needAction')}
					{renderContractsStatusButton('sign')}
					{renderContractsStatusButton('locationEnd')}
				</div>

				<div className={'globalListHeader'}>
					<Button
						size={'sm'}
						type={'tertiary-gray'}
						iconBefore={img_filter}
						onClick={() => set_modal('filter')}
					>
						{TEXTE.global.filter}
						{filterCount > 0 && <Badge size={'sm'} color={'gray'} text={filterCount} />}
					</Button>

					<Input
						fullWidth
						value={search}
						onChange={set_search}
						placeholder={TEXTE.global.search}
						before={<img src={img_search} alt={TEXTE.users.search} />}
					/>
				</div>

				<Table
					data={table}
					load={contractsLoad && contracts.length === 0}
					onClickRow={({original}) => {
						if (original)
							navigate(`/contracts/${original._id}/${'i' in original ? original.i : 0}`);
					}}
				/>
			</div>
		);
	}

	function renderDefaultView() {
		return (
			<div className={'devicesSelectMain'}>
				<Icon
					size={40}
					iconBorder
					iconSize={24}
					borderSize={8}
					iconColor={view === 'confirm' ? '#000' : '#6938EF'}
					type={view === 'confirm' ? 'success' : 'primary'}
					icon={view === 'confirm' ? img_faceSmile : img_search}
				/>
				<div className={'devicesSelectTitle'}>
					{TEXTE.contract.selectContract}
				</div>
				<div className={'devicesSelectText'}>
					{TEXTE.contract.selectContractText}
				</div>

				<MultiInputContainer>
					<Button
						size={'lg'}
						type={'primary'}
						onClick={() => navigate('/order/catalog')}
					>
						{TEXTE.header.newCommand}
					</Button>

					<Button
						size={'lg'}
						type={'secondary-gray'}
						onClick={() => navigate('/contracts/add')}
					>
						{TEXTE.contract.addContract}
					</Button>
				</MultiInputContainer>
			</div>
		);
	}

	function renderAboutSection() {
		let render = <div />;

		if (menu === 'details') {
			const isValala = editData && ('funderList' in editData);
			const funders: string[] = [];

			if (isValala) {
				editData.funderList.forEach((funder) => {
					if (funder.funder?.name && !funders.some(f => f === funder.funder?.name))
						funders.push(funder.funder.name);
				});
			}

			render = (
				<div className={'deviceDetailsContainer'}>
					<MultiInputContainer>
						<Input
							readOnly={isValala}
							type={'date'}
							value={contractStart}
							onChange={set_contractStart}
							title={TEXTE.global.contractStart}
						/>
						<Input
							readOnly={isValala}
							type={'date'}
							value={contractEnd}
							onChange={set_contractEnd}
							title={TEXTE.global.contractEnd}
						/>
					</MultiInputContainer>

					<MultiInputContainer>
						<Input
							readOnly={isValala}
							min={0}
							value={price}
							type={'number'}
							onChange={set_price}
							title={TEXTE.contract.monthlyPrice}
							notFocusValue={price ? ft_formatPrice(price) : undefined}
						/>
						{isValala &&
							<Input
								readOnly
								value={funders.join(', ')}
								title={TEXTE.global.funder}
							/>
						}
					</MultiInputContainer>

					<Textarea
						value={description}
						style={{height: 156}}
						onChange={set_description}
						title={TEXTE.global.descriptionAndNote}
					/>
				</div>
			);
		} else if (menu === 'historical')
			render = <DevicesHistorical />;
		else if (menu === 'documents' && editData)
			render = <ContractDocument editData={editData} />;

		return (
			<div className={'globalDataAboutContainer'}>
				<Tabs
					data={MENU_DATA}
					actual={menu}
					onChange={set_menu}
				/>

				{render}
			</div>
		);
	}

	function renderContractData() {
		if (!editData)
			return <div className={'usersData'} />;

		const renderDevices: React.ReactElement[] = [];
		const renderTags: React.ReactElement[] = [];
		const isValala = 'funderList' in editData;
		let contractPrice = editData.price;

		if (isValala) {
			contractPrice = editData.funderList[indexFunder].price;

			const devicesList: FunderDevice[] = [];
			const replacementList: FunderDevice[] = [];

			const getLastReplacement = (list: FunderDevice[], device: Device): FunderDevice | false => {
				const index = list.findIndex(f => f.device.replacement?.replaces === device._id
					|| device.replacement?.replaceBy === f.device._id
					|| f.device.data?.oldCompany?.find(f2 => f2.replaces && f2.contract === editData._id)?.replaces === device._id
				);

				if (index === -1)
					return false;

				const actual = list.splice(index, 1);
				return getLastReplacement(list, actual[0].device) || actual[0];
			};

			editData.funderList[indexFunder]?.devices?.forEach((device) => {
				if (device.device.replacement?.replaces || device.device.data?.oldCompany?.find(f => f.replaces && f.contract === editData._id))
					replacementList.push(device);
				else
					devicesList.push(device);
			});

			devicesList.forEach((device) => {
				const lastReplacement = getLastReplacement([...replacementList], device.device);

				renderDevices.push(
					<DeviceCard
						link
						forceUser
						hideBadge
						device={device.device}
						disabled={Boolean(lastReplacement)}
						hideAssign={Boolean(lastReplacement)}
						duration={editData.funderList[indexFunder].duration}
						showPrice={lastReplacement ? TEXTE.contract.contractStatus.locationEnd : true}
					/>
				);

				if (lastReplacement) {
					renderDevices.push(<Badge style={{alignSelf: 'center', margin: 10}} text={'Remplacé par'} />);
					renderDevices.push(
						<DeviceCard
							link
							forceUser
							hideBadge
							showPrice
							device={lastReplacement.device}
							duration={editData.funderList[indexFunder].duration}
						/>
					);
				}
			});
		} else {
			devicesWithLines.forEach((device) => {
				if (editData.devices.find(find => find === device._id)) {
					renderDevices.push(
						<DeviceCard
							link
							showPrice
							forceUser
							hideBadge
							device={device}
						/>
					);
				}
			});
		}

		editData.tags?.forEach((tag => (
			renderTags.push(<Badge text={tag} size={'sm'} color={'tags'} />)
		)));

		return (
			<div className={'globalDataContainer'}>

				<Section>
					<div className={'globalDataContainerItem'}>
						<Icon
							size={40}
							iconBorder
							icon={img_file}
							borderSize={0}
						/>
						<div className={'text-sm'}>
							<ContractStatus
								contract={editData}
								indexFunder={isValala ? indexFunder : undefined}
							/>
							<div>
								<span className={'weight-500 gray-900'}>{isValala ? `${TEXTE.contract.contractNumber} ${editData.number}` : editData.name}</span>
							</div>
							{editData.data?.creationDate &&
								<span className={'gray-600 weight-400'}>{`${TEXTE.contract.the} ${moment(editData.data.creationDate).format('DD/MM/YYYY')}`}</span>
							}
						</div>

						<div className={'globalDataContainerItemTags'}>
							{renderTags}
						</div>

						<div className={'flex'}>
							<span className={'weight-500 primary-600'}>{`${ft_formatPrice(contractPrice)} HT`}</span>
						</div>

						<Button
							absoluteRight
							absoluteHideIcon
							type={'tertiary-gray'}
							absolute={ACTIONS_LIST}
							onChangeAbsolute={(action) => {
								if (action === 'tags')
									return set_modal('tags');
							}}
						>
							<Icon icon={img_dotsVertical} />
						</Button>
					</div>
				</Section>

				<Section
					title={TEXTE.contract.contractDevice}
					afterTitle={<DevicesBadge contract={editData} indexContractFunder={isValala ? indexFunder : undefined} />}
				>
					<div className={'usersDataDevices'}>
						{renderDevices}
					</div>
				</Section>

				<Section title={TEXTE.global.about}>
					{renderAboutSection()}
				</Section>

				<ButtonStickyContainer>
					<Button
						fullWidth
						disabled={!change}
						isLoad={usersPutLoad}
						onClick={editContract}
					>
						{TEXTE.global.saveChange}
					</Button>
				</ButtonStickyContainer>
			</div>
		);
	}

	return (
		<div className={'globalMainContainer'}>
			{!view && renderContractsList()}

			{(view === 'confirm') && renderDefaultView()}
			{(view === 'edit') && renderContractData()}

			{modal === 'filter' &&
				<ModalFilter
					isContract
					filters={filters}
					tagsList={tagsList}
					setFilters={set_filters}
					onClose={() => set_modal(null)}
				/>
			}

			{(modal === 'tags' && editData) &&
				<ModalTags
					load={load}
					allTags={tagsList}
					onConfirm={editTags}
					tags={editData.tags || []}
					onClose={() => set_modal(null)}
				/>
			}
		</div>
	);
}

export default Contracts;
